<template>
  <div id="container">
    <SelectUserRole ref="selectUserRoleRef" @handle-close="startAuth" />
    <PowerBIReportEmbed v-if="embedConfig != null" :embedConfig="embedConfig" />
  </div>
</template>

<script setup>
import { onMounted, ref, nextTick } from "vue";
import { useToast } from "primevue/usetoast";
import { useRouter } from "vue-router";
// https://www.npmjs.com/package/powerbi-client
import * as pbi from "powerbi-client";
// https://www.npmjs.com/package/powerbi-client-vue-js?activeTab=readme
import { PowerBIReportEmbed } from "powerbi-client-vue-js";
import { useAxios } from "@sttedra_ui/framework";
import { checkTokenExpired } from "@/composables/_commons/auth.js";

import SelectUserRole from "@/pages/_root/views/reports-details/SelectUserRole.vue";

// #region Props, Emits
const props = defineProps({
  report: {
    type: Object,
    required: true,
  },
});
// #endregion

// #region  Params, Ref, Reactives, Computed
const toast = useToast();
const { base } = useAxios();
const embedConfig = ref(null);
const tokenExpiry = ref(null);
const router = useRouter();
const selectUserRoleRef = ref(null);
const roles = ref([]);

const MINUTES_BEFORE_EXPIRATION = 10;
const INTERVAL_TIME = 300000; // Cada 5 minutos comprueba si quedan 10 o menos para expirar
// #endregion

// #region Functions
onMounted(() => {
  if (!props.report.esPublico) {
    const rolesInforme = props.report.rolesInforme;
    const tieneRoles = props.report.tieneRoles;

    if (tieneRoles) {
      rolesInforme.sort((a, b) => a.orden - b.orden);

      if (rolesInforme.length === 1) {
        startAuth([rolesInforme[0].nombre]);
        return;
      }

      rolesInforme.forEach((role) => {
        roles.value.push(role.nombre);
      });

      if (rolesInforme.length > 1) {
        selectUserRoleRef.value.showModal(roles.value);
      } else {
        startAuth([]);
      }
    } else {
      startAuth([]);
    }
  } else {
    startAuth([]);
  }
});

// Step 1
async function startAuth(roles) {
  try {
    const response = await base.post(
      `/PowerBI/TokenCreation`,
      {
        workspaceId: props.report.guidWorkspace,
        reportId: props.report.guidReportPowerBI,
        roles,
      },
      { timeout: 20000 },
    );

    selectUserRoleRef.value?.closeModal();

    autenticateUser(response.data, roles);

    if (selectUserRoleRef.value) {
      selectUserRoleRef.value.loading = false;
    }
  } catch (error) {
    if (selectUserRoleRef.value) {
      selectUserRoleRef.value.loading = false;
    }

    console.error("Error starting authentication:", error);

    if (
      error?.response?.data?.errors?.length &&
      error?.response?.data?.errors[0].message
    ) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response.data.errors[0].message,
        life: 3000,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Ha ocurrido un error inesperado",
        life: 3000,
      });
    }

    router.push({
      name: "Public reports",
    });
  }
}

// Step 2
async function autenticateUser(data, roles) {
  try {
    loadPbiReport(
      data.embedReport[0].embedUrl,
      data.embedToken.token,
      data.embedReport[0].reportId,
      data.embedReport[0].groupId,
      data.embedToken.expiration,
      data.type,
      roles,
    );
  } catch (error) {
    console.error("Error authenticating user:", error);

    router.push({
      name: "Public reports",
    });
  }
}

// Step 3
async function loadPbiReport(
  embedUrl,
  token,
  reportId,
  workspaceId,
  tokenExpiration,
  type,
  roles,
) {
  const filterPaneEnabled = props.report.mostrarFiltros
    ? props.report.mostrarFiltros
    : false;

  const navContentPaneEnabled = props.report.mostrarTabs
    ? props.report.mostrarTabs
    : false;

  embedUrl = new URL(embedUrl);

  // If window location has filter params, add them to the embed URL
  const urlParams = new URLSearchParams(window.location.search);

  if (urlParams.size > 0) {
    let filter = urlParams.get("filter");
    filter = filter.replace(/ /g, "%20");

    embedUrl.searchParams.set("filter", filter);
  }

  // const target = "DIM_Estaciones/des_estacion";
  // const operator = "in";
  // const value = "('BARCELONA')";

  // let filterValue = `${target} ${operator} ${value}`;
  // filterValue = filterValue.replace(/ /g, "%20");

  // embedUrl.searchParams.set("filter", filterValue);

  embedConfig.value = {
    type: type.toLowerCase(),
    id: reportId,
    embedUrl: embedUrl.href,
    accessToken: token,
    tokenType: pbi.models.TokenType.Embed,
    permissions: pbi.models.Permissions.Read,
    settings: {
      // background: pbi.models.BackgroundType.Transparent,
      // layoutType: pbi.models.LayoutType.MobilePortrait,
      filterPaneEnabled,
      navContentPaneEnabled,
    },
  };

  // Use the token expiry to regenerate Embed token for seamless end user experience
  // Refer https://aka.ms/RefreshEmbedToken
  tokenExpiry.value = tokenExpiration;

  // Embed Power BI report when Access token and Embed URL are available
  const dashboardContainer = document.getElementById("container");

  nextTick(async () => {
    let report = powerbi.embed(dashboardContainer, embedConfig.value);

    // Set an interval to check the access token expiration, and update if needed
    setInterval(
      () => checkTokenAndUpdate(reportId, workspaceId, roles),
      INTERVAL_TIME,
    );

    // Add a listener to make sure token is updated after tab was inactive
    document.addEventListener("visibilitychange", function () {
      // Check the access token when the tab is visible
      if (!document.hidden) {
        checkTokenAndUpdate(reportId, workspaceId, roles);
      }
    });

    // Clear any other loaded handler events
    report.off("loaded");

    // Triggers when a report schema is successfully loaded
    report.on("loaded", async () => {
      const prefilters = props.report.prefiltros;
      const prefiltersFormatted = [];

      if (prefilters.length && !urlParams.has("filter")) {
        prefilters.forEach((prefilter) => {
          const tableColumn = prefilter.nombreTablaColumna.split("/");
          const values = prefilter.valor.split(",");

          const basicFilter = {
            target: {
              table: tableColumn[0],
              column: tableColumn[1],
            },
            operator: prefilter.operador,
            values,
          };

          prefiltersFormatted.push(basicFilter);
        });

        // const basicFilter = {
        //   target: {
        //     table: "DIM_Estaciones",
        //     column: "des_estacion",
        //   },
        //   operator: "In",
        //   values: ["BARCELONA"],
        // };

        const powerBi = powerbi.get(dashboardContainer);

        try {
          await powerBi.setFilters(prefiltersFormatted);
        } catch (error) {
          console.error("Error applying filter:", error);
        }
      }
    });

    // Clear any other rendered handler events
    report.off("rendered");

    // Triggers when a report is successfully embedded in UI
    report.on("rendered", () => {});

    // Clear any other error handler events
    report.off("error");

    // Handle embed errors
    report.on("error", function (event) {
      let errorMsg = event.detail;

      // Use errorMsg variable to log error in any destination of choice
      console.error(errorMsg);
      return;
    });
  });
}

// Step 4
async function checkTokenAndUpdate(reportId, workspaceId, roles) {
  // Get the current time
  const currentTime = Date.now();
  const expiration = Date.parse(tokenExpiry.value);

  // Time until token expiration in milliseconds
  const timeUntilExpiration = expiration - currentTime;
  const timeToUpdate = MINUTES_BEFORE_EXPIRATION * 60 * 1000;

  await checkTokenExpired();

  // Update the token if it is about to expired
  if (timeUntilExpiration <= timeToUpdate) {
    updateToken(reportId, workspaceId, roles);
  }
}

// Step 5
async function updateToken(reportId, workspaceId, roles) {
  try {
    await checkTokenExpired();

    const response = await base.post(
      `/PowerBI/TokenRenewal`,
      {
        workspaceId: workspaceId,
        reportId: reportId,
        roles,
      },
      { timeout: 20000 },
    );

    tokenExpiry.value = response.data.expiration;

    const dashboardContainer = document.getElementById("container");

    nextTick(() => {
      let report = powerbi.get(dashboardContainer);

      report.setAccessToken(response.data.token);
    });
  } catch (error) {
    console.error("Error updating token:", error);

    router.push({
      name: "Public reports",
    });
  }
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
#container {
  height: calc(var(--header-footer-height) - 4px) !important;
  top: var(--header-footer-height);

  :deep(iframe) {
    border: 0;
  }
}
</style>
